import { useAppSelector } from '@/app/hooks';
import { selectIsBuffetDineIn, selectTableSession } from './tableSessionSlice';
import { useGetTableSessionQuery } from './tableSessionApi';
import { useTableSessionPollInterval } from './useTableSessionPollInterval';

export interface BuffetRoundGateData {
  /** Effective max items per round (covers-scaled server-side). null ⇒ no budget gate. */
  roundItemBudget: number | null;
  /** Max rounds for the session. null ⇒ unlimited. */
  maxRounds: number | null;
  /** Rounds already sent (server's non-cancelled order count). */
  roundCount: number;
  /** Seconds left before a new round may start. 0 ⇒ no cooldown active. */
  cooldownSecondsRemaining: number;
}

const EMPTY: BuffetRoundGateData = {
  roundItemBudget: null,
  maxRounds: null,
  roundCount: 0,
  cooldownSecondsRemaining: 0,
};

/**
 * SC-221/222/223 — live buffet round limits + counters for cart-time gating.
 *
 * Subscribes to the existing 5s table-session poll (deduped by RTK Query with
 * TablePanel's / useSharedTableCart's subscriptions) for any buffet dine-in
 * session — shared or not. Returns null limits outside buffet dine-in so callers
 * apply no add-time gate. Visibility-gated like the other pollers so a hidden tab
 * doesn't keep the poll alive.
 */
export function useBuffetRoundGate(): BuffetRoundGateData {
  const isBuffetDineIn = useAppSelector(selectIsBuffetDineIn);
  const session = useAppSelector(selectTableSession);
  const guestToken = session?.guestToken ?? '';

  const pollingInterval = useTableSessionPollInterval();

  const { data } = useGetTableSessionQuery(
    {
      restaurantUuid: session?.restaurantUuid ?? '',
      sessionUuid: session?.sessionUuid ?? '',
      guestToken,
    },
    {
      skip: !isBuffetDineIn || !guestToken,
      pollingInterval,
    },
  );

  if (!isBuffetDineIn || !data) return EMPTY;

  return {
    roundItemBudget: data.buffet_round_item_budget ?? null,
    maxRounds: data.buffet_max_rounds ?? null,
    roundCount: data.round_count ?? session?.roundCount ?? 0,
    cooldownSecondsRemaining: data.cooldown_seconds_remaining ?? 0,
  };
}
